import { useState } from "react";
import { Plus, Trash2, TrendingUp, CheckCircle2, Circle } from "lucide-react";
import { useFinancialState } from "@/context/StateContext";
import { computeMetrics, fmtMoney, uid } from "@/lib/finance";
import { PageHeader, SectionCard, EmptyState, AddButton, KpiCard, ProgressBar } from "@/components/shared";
import { FormModal, TextField, NumberField } from "@/components/forms";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

export default function WealthPlan() {
  const { state, update, loading } = useFinancialState();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  if (loading || !state) return <Skeleton className="h-96 rounded-[14px]" />;

  const wp = state.wealthPlan || { notes: "", milestones: [] };
  const m = computeMetrics(state);
  const setWp = (patch, summary) => update((prev) => ({ ...prev, wealthPlan: { ...prev.wealthPlan, ...patch } }), summary);

  const saveMilestone = () => {
    if (!form.title) return toast.error("Title is required");
    const ms = { ...form, target: Number(form.target || 0) };
    setWp({ milestones: (wp.milestones || []).some((x) => x.id === ms.id) ? wp.milestones.map((x) => (x.id === ms.id ? ms : x)) : [...(wp.milestones || []), ms] }, `Saved milestone: ${ms.title}`);
    setOpen(false); toast.success("Milestone saved");
  };
  const toggle = (id) => setWp({ milestones: wp.milestones.map((x) => (x.id === id ? { ...x, done: !x.done } : x)) }, "Updated milestone");
  const removeMs = (id) => setWp({ milestones: wp.milestones.filter((x) => x.id !== id) }, "Removed milestone");

  const savingsCapacity = Math.max(0, m.income - m.txExpense);

  return (
    <div>
      <PageHeader title="Wealth plan" subtitle="Your long-term roadmap and the milestones that define it." testid="wealthplan-header"
        actions={<AddButton onClick={() => { setForm({ id: uid(), title: "", target: "", done: false }); setOpen(true); }} testid="add-milestone-button"><Plus className="h-4 w-4 mr-1.5" />Add milestone</AddButton>} />

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <KpiCard label="Net worth" value={fmtMoney(m.netWorth)} icon={TrendingUp} accent testid="wp-networth" />
        <KpiCard label="Monthly surplus" value={fmtMoney(savingsCapacity)} sub="Income minus spending" testid="wp-surplus" />
        <KpiCard label="Milestones done" value={`${(wp.milestones || []).filter((x) => x.done).length}/${(wp.milestones || []).length}`} testid="wp-milestones" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-7">
          <SectionCard title="Milestones" testid="milestones-card">
            {(wp.milestones || []).length ? (
              <div className="space-y-2">
                {wp.milestones.map((ms) => (
                  <div key={ms.id} className="flex items-center justify-between rounded-[10px] border border-[color:var(--border-subtle)] bg-white px-3 py-2.5" data-testid={`milestone-row-${ms.id}`}>
                    <button onClick={() => toggle(ms.id)} className="flex items-center gap-3 text-left" aria-label="Toggle">
                      {ms.done ? <CheckCircle2 className="h-5 w-5 text-[var(--green)]" /> : <Circle className="h-5 w-5 text-[var(--text-faint)]" />}
                      <div><div className={`text-sm font-medium ${ms.done ? "line-through text-[var(--text-faint)]" : "text-[var(--text)]"}`}>{ms.title}</div>{ms.target > 0 && <div className="text-[11px] text-[var(--text-faint)]">Target {fmtMoney(ms.target)}</div>}</div>
                    </button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-[var(--danger)]" onClick={() => removeMs(ms.id)} aria-label="Delete"><Trash2 className="h-3 w-3" /></Button>
                  </div>
                ))}
              </div>
            ) : <EmptyState icon={TrendingUp} title="No milestones" message="Define the milestones on your path to wealth." />}
          </SectionCard>
        </div>
        <div className="lg:col-span-5">
          <SectionCard title="Strategy notes" subtitle="Your playbook" testid="strategy-card">
            <Textarea value={wp.notes || ""} onChange={(e) => setWp({ notes: e.target.value }, "Updated strategy notes")} rows={10}
              placeholder="Outline your wealth-building strategy, principles, and priorities…"
              className="bg-white border-[color:var(--border-subtle)] resize-none" data-testid="strategy-notes" />
          </SectionCard>
        </div>
      </div>

      {form && (
        <FormModal open={open} onOpenChange={setOpen} title={(wp.milestones || []).some((x) => x.id === form.id) ? "Edit milestone" : "Add milestone"} onSubmit={saveMilestone} testid="milestone-modal">
          <TextField label="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} full testid="milestone-title-input" />
          <NumberField label="Target value (optional)" value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })} full />
        </FormModal>
      )}
    </div>
  );
}
